import { useCallback } from "react";
import { clipboardToMarkdown } from "./htmlToMarkdown";

/**
 * Paste rendered content into a textarea without losing its structure.
 *
 * A textarea accepts only the clipboard's text/plain flavour. When somebody
 * copies rendered output from a browser, the styled text/html flavour is
 * discarded along with every heading, emphasis and link target it carried, and
 * what lands in the database is already flat. This reads the HTML the textarea
 * would have thrown away and inserts markdown in its place.
 *
 * Attach it only to fields that render markdown back. Storing syntax that is
 * then displayed literally is worse than storing flat text, which is why
 * contacts.background and companies.description do not use it.
 */
// TextInput renders an <input> without `multiline` and a <textarea> with it,
// and its props type is the intersection of both. The handler has to accept
// either to be assignable at all, and the insertion below works on both.
type TextField = HTMLTextAreaElement | HTMLInputElement;

export function usePasteAsMarkdown() {
  return useCallback((event: React.ClipboardEvent<TextField>) => {
    const markdown = clipboardToMarkdown(
      event.clipboardData.getData("text/html"),
    );
    if (markdown === null) return;

    event.preventDefault();
    insertText(event.currentTarget, markdown);
  }, []);
}

/** Inserts `text` at the caret of a text field, replacing any selection. */
export function insertText(element: TextField, text: string): void {
  // execCommand is the only insertion that leaves the browser's own undo stack
  // intact, so Ctrl+Z after a paste still does what the user expects. It is
  // deprecated and has no replacement; the manual path covers the browsers and
  // test environments that do not implement it.
  try {
    if (document.execCommand?.("insertText", false, text)) return;
  } catch {
    // fall through
  }

  const { selectionStart, selectionEnd, value } = element;
  const start = selectionStart ?? value.length;
  const end = selectionEnd ?? start;

  // React tracks a controlled input's last value on the node itself, so
  // assigning element.value directly makes it conclude nothing changed and drop
  // the event. Going through the prototype setter keeps the change visible.
  const setValue = Object.getOwnPropertyDescriptor(
    element instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype,
    "value",
  )?.set;
  setValue?.call(element, value.slice(0, start) + text + value.slice(end));

  element.setSelectionRange(start + text.length, start + text.length);
  element.dispatchEvent(new Event("input", { bubbles: true }));
}
